/**
 * @plerity/csv — shared types.
 *
 * Tier 1 (contracts). Zero runtime dependencies. No React / DOM / RN at module scope.
 */

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/** One parsed record (a CSV "row"). */
export interface CsvRow {
  /**
   * 1-based record number counting the header as 1 and counting blank lines — the number a
   * spreadsheet shows in its row gutter. Use this in user-facing error messages.
   */
  index: number;
  /** 1-based physical line where the record starts (differs from `index` after a quoted newline). */
  line: number;
  cells: string[];
}

export type CsvDelimiter = "," | ";" | "\t" | "|";

/** Encodings the decoder accepts. `auto` sniffs a BOM, then tries UTF-8, then falls back to windows-1252. */
export type CsvEncoding = "auto" | "utf-8" | "utf-16le" | "utf-16be" | "windows-1252" | "iso-8859-1";

/** Anything `parseFile` can read. Bytes are decoded; strings are used as-is. */
export type CsvSource =
  | string
  | Uint8Array
  | ArrayBuffer
  | Blob
  | ReadableStream<Uint8Array | string>
  | AsyncIterable<Uint8Array | string>
  | Iterable<Uint8Array | string>;

export interface ParseOptions {
  /** Field delimiter. Default `"auto"`: detected from the first few records (`, ; <tab> |`). */
  delimiter?: CsvDelimiter | "auto";
  /** Text encoding. Default `"auto"`. */
  encoding?: CsvEncoding;
  /** Skip lines with no content (default `true`). They still advance `CsvRow.index`. */
  skipEmptyLines?: boolean;
  /**
   * Hard cap on one field's length in characters (default 1,000,000). A runaway unterminated quote
   * would otherwise buffer the rest of the file; exceeding the cap throws `CsvParseError`.
   */
  maxFieldLength?: number;
  /** Called after each decoded chunk with the cumulative number of source bytes read. */
  onProgress?: (bytesRead: number) => void;
  /** Aborts the parse; the iterator rejects with an `AbortError`. */
  signal?: AbortSignal;
}

/** What the decoder/sniffer learned about the stream. Populated progressively while iterating. */
export interface CsvMeta {
  /** Resolved delimiter (`null` until the first chunk is sniffed). */
  delimiter: CsvDelimiter | null;
  /** Resolved encoding (`null` until the first chunk is sniffed). */
  encoding: Exclude<CsvEncoding, "auto"> | null;
  /** A byte-order mark was found and removed. */
  hadBom: boolean;
  /** Bytes read from the source so far. */
  bytesRead: number;
  /** Records yielded so far (blank lines excluded). */
  rowsYielded: number;
  /** Blank lines skipped so far. */
  blankLinesSkipped: number;
  /** U+FFFD replacement characters produced while decoding (bytes invalid in the chosen encoding). */
  replacementChars: number;
}

/** The iterable `parseFile` returns. Single-use. */
export interface CsvParseHandle extends AsyncIterable<CsvRow> {
  readonly meta: CsvMeta;
}

export type CsvParseErrorCode = "unterminated_quote" | "field_too_large" | "already_consumed";

export class CsvParseError extends Error {
  readonly code: CsvParseErrorCode;
  /** 1-based line where the offending record started, when known. */
  readonly line: number | null;
  constructor(code: CsvParseErrorCode, message: string, line: number | null = null) {
    super(message);
    this.name = "CsvParseError";
    this.code = code;
    this.line = line;
  }
}

// ---------------------------------------------------------------------------
// Headers + mapping
// ---------------------------------------------------------------------------

export interface HeaderRename {
  column: number;
  from: string;
  to: string;
  reason: "empty" | "duplicate";
}

export interface InferredHeaders {
  /** Cleaned, unique header labels (always `columns` long). */
  headers: string[];
  /** Whether the first row looks like a header row (all labels, next row looks like data). */
  hasHeader: boolean;
  /** Headers we had to change (empty → `Column N`, duplicates → `Name (2)`). Show these to the user. */
  renamed: HeaderRename[];
}

/** A field the file's columns can map to. */
export interface CsvTargetField {
  /** Key in the validated row object (matches the schema's key). */
  key: string;
  /** Human label; also matched against headers. */
  label: string;
  required?: boolean;
  /** Extra header spellings that should match this field ("e-mail", "Phone number", …). */
  aliases?: readonly string[];
}

export type MatchMethod = "exact" | "case-insensitive" | "fuzzy" | "manual" | "none";

export interface ColumnMappingEntry {
  /** 0-based column index in the file. */
  column: number;
  /** The header label the user sees. */
  header: string;
  /** Target field key, or `null` to ignore this column. */
  field: string | null;
  /** 0..1. `1` exact, `0.9–0.95` case/punctuation-insensitive, `<0.9` fuzzy, `0` none. */
  confidence: number;
  method: MatchMethod;
  /**
   * `false` for fuzzy suggestions until the user confirms them. Exact / case-insensitive matches and
   * columns the user set by hand are confirmed. An unconfirmed entry must not be imported silently.
   */
  confirmed: boolean;
}

export type ColumnMapping = readonly ColumnMappingEntry[];

export interface MapColumnsOptions {
  /** Minimum similarity for a fuzzy suggestion (default 0.72). */
  fuzzyThreshold?: number;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** The slice of a zod (v3 or v4) schema `validateRows` needs. Structural, so `csv` has no zod dependency. */
export interface RowSchema<T> {
  safeParse(
    input: unknown
  ): { success: true; data: T } | { success: false; error: { issues: ReadonlyArray<RowSchemaIssue> } };
}

export interface RowSchemaIssue {
  path: ReadonlyArray<PropertyKey>;
  message: string;
  /** zod's issue code; `invalid_type` on an absent key is reported as "Required". */
  code?: string;
}

export type CsvIssueCode = "schema" | "shape" | "formula";

export interface CsvRowIssue {
  /** Spreadsheet-style row number (`CsvRow.index`; header = 1). */
  row: number;
  /** Physical line of the record start. */
  line: number;
  /** Target field key, the CSV header, or `null` for whole-row problems. */
  field: string | null;
  message: string;
  code: CsvIssueCode;
}

/** `flag` keeps the value and reports a warning; `neutralise` prefixes it with `'`; `reject` is a row error. */
export type FormulaPolicy = "flag" | "neutralise" | "reject";

export interface ValidatedRow<T> {
  index: number;
  line: number;
  data: T;
}

export interface ValidateOptions<T> {
  /** Trim whitespace around each cell (default `true`). */
  trim?: boolean;
  /** Treat an empty cell as an absent key so optional fields work (default `true`). */
  emptyAsUndefined?: boolean;
  /** Default `flag`. */
  formulaPolicy?: FormulaPolicy;
  /** Cap on stored errors / warnings (default 1000). Counts stay exact; `*Truncated` says the list is cut. */
  maxIssues?: number;
  /** Keep valid rows in the result (default `true`). Set `false` and use `onValid` to stream. */
  collectRows?: boolean;
  /** Called for every valid row (in file order). */
  onValid?: (row: ValidatedRow<T>) => void;
}

export interface ValidateResult<T> {
  /** `errorCount === 0`. */
  ok: boolean;
  /** Data records seen (header excluded, rows with every mapped cell empty excluded). */
  total: number;
  /** Rows whose cells were all empty (`,,,` lines from a spreadsheet export). Counted, not validated. */
  blankRows: number;
  validCount: number;
  /** Rows with at least one error. */
  errorCount: number;
  warningCount: number;
  rows: ValidatedRow<T>[];
  errors: CsvRowIssue[];
  warnings: CsvRowIssue[];
  errorsTruncated: boolean;
  warningsTruncated: boolean;
}

// ---------------------------------------------------------------------------
// Import target (consumed by the ImportWizard)
// ---------------------------------------------------------------------------

export interface CsvCommitOptions {
  /** `true`: validate server-side and report, but write nothing. */
  dryRun: boolean;
}

export interface CsvCommitResult {
  /** Rows created (or, for a dry run, rows that WOULD be created). */
  created: number;
  updated?: number;
  skipped?: number;
  /** Rows the target refused, keyed by `ValidatedRow.index`. Never dropped silently. */
  errors?: ReadonlyArray<{ row: number; field?: string | null; message: string }>;
}

export interface CsvImportTarget<T> {
  /** Plural noun for UI copy: "contacts". */
  entityName: string;
  fields: readonly CsvTargetField[];
  schema: RowSchema<T>;
  /**
   * Persist (or, with `dryRun`, check) a batch. Throw to signal a transport failure; return `errors`
   * for rows the server rejected.
   */
  commit(rows: ReadonlyArray<ValidatedRow<T>>, options: CsvCommitOptions): Promise<CsvCommitResult>;
  /** Example rows keyed by field key; powers "Download template". */
  sample?: ReadonlyArray<Readonly<Record<string, string>>>;
}
